import React, { useContext, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import Button from "../../Button/Button";
import TextInput from "../../TextInput/TextInput";
import SelectWithBackendSearch from "../../Select/SelectWithBackendSearch";
import { formatPhoneNumber, validateEmail, validatePhoneOrFaxNumber } from "../../../utils";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { OfficeService } from "../../../services/office.service";
import { ContactService } from "../../../services/contact.service";
import { ToastVariants } from "../../../models";
import { setRequestData } from "../../../redux/slices/OfficeRequestSlice";
import { UserType } from "../../../services/user.service";
import { Contact, ContactMethodType } from "../../../models/contact.model";

const Recipients = ({
  onClickCancel,
  setIsLoading,
  isLoading,
}: {
  onClickCancel: (val?: boolean) => void;
  setIsLoading: (val: boolean) => void;
  isLoading?: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { assignRequestError, requestData, loading } = useSelector((state: RootState) => state.officeRequest);
  const { patientRequestData } = useSelector((state: RootState) => state.user);
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const request = currentUser?.userType === UserType.Patient ? patientRequestData : requestData;
  const [errorInitiated, setErrorInitiated] = useState(false);
  const toast = useContext(ToastContext);
  const [errors, setErrors] = useState({
    name: false,
    emailOrFax: false,
  });
  const [name, setname] = useState(request?.recipientName || "");
  const [emailOrFax, setEmailOrFax] = useState(request?.recipientContact || "");

  // New state for contact search
  const [officeContacts, setOfficeContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedContactMethod, setSelectedContactMethod] = useState<ContactMethodType | null>(null);
  const [contactsLoading, setContactsLoading] = useState(false);

  // Fetch contacts on mount and when search changes
  const fetchContacts = async (searchTerm?: string) => {
    if (request?.officeId) {
      setContactsLoading(true);
      try {
        const resp = await ContactService.searchContacts(request.officeId, 0, 200, undefined, searchTerm);
        if (resp && resp?.content) {
          setOfficeContacts(resp.content);
        }
      } finally {
        setContactsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [request?.officeId]);

  useEffect(() => {
    if (searchValue) {
      fetchContacts(searchValue);
    } else {
      fetchContacts();
    }
  }, [searchValue]);

  const handleContactSelect = (contact: Contact) => {
    setSelectedContact(contact);
    setname(contact.company || `${contact.firstName} ${contact.lastName}`.trim());
    // Auto-select contact method based on available info
    if (contact.email && !contact.fax) {
      setSelectedContactMethod(ContactMethodType.Email);
      setEmailOrFax(contact.email);
    } else if (contact.fax && !contact.email) {
      setSelectedContactMethod(ContactMethodType.Fax);
      setEmailOrFax(formatPhoneNumber(contact.fax));
    } else if (contact.email) {
      // Default to email if both available
      setSelectedContactMethod(ContactMethodType.Email);
      setEmailOrFax(contact.email);
    }
    setErrors({ name: false, emailOrFax: false });
  };

  const handleContactMethod = (method: ContactMethodType) => {
    setSelectedContactMethod(method);
    if (selectedContact) {
      if (method === ContactMethodType.Email) {
        setEmailOrFax(selectedContact.email || "");
      } else {
        setEmailOrFax(formatPhoneNumber(selectedContact.fax || ""));
      }
    }
  };

  const formatAddress = (contact: Contact): string => {
    const parts = [contact.street1, contact.city, contact.province, contact.postalCode].filter(Boolean);
    return parts.join(", ");
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (!text || text.length <= maxLength) return text || "";
    return text.substring(0, maxLength) + "...";
  };

  const handleNameChange = (value: string) => {
    setname(value);
    setSelectedContact(null); // Clear selected contact when manually editing
    let isValid = value !== "";
    if (value === "") {
      isValid = emailOrFax === "";
    }

    setErrors({ emailOrFax: emailOrFax === "" && value.length > 0, name: !isValid });
  };

  const validateForm = () => {
    return !errors.emailOrFax && !errors.name && name.trim() !== "" && emailOrFax.trim() !== "";
  };

  const handleEmailOrFaxInput = (event: React.ChangeEvent<HTMLInputElement>, cursorPosition: number) => {
    const value = event.currentTarget.value.trim();
    setSelectedContact(null); // Clear selected contact when manually editing
    if (validateEmail(value)) {
      setErrors({ name: value.length > 0 && name === "", emailOrFax: false });
      setEmailOrFax(value);
    } else if (validatePhoneOrFaxNumber(formatPhoneNumber(value))) {
      setErrors({ name: value.length > 0 && name === "", emailOrFax: false });
      setEmailOrFax(formatPhoneNumber(value));
    } else {
      setErrors({ name: value.length > 0 && name === "", emailOrFax: true });
      setEmailOrFax(value);
    }
  };

  const saveRecipient = async () => {
    if (!name.trim() || !emailOrFax.trim()) {
      setErrors({ name: !name.trim(), emailOrFax: !emailOrFax.trim() });
      return;
    }
    if (request?.officeId && request?.requestId) {
      setIsLoading(true);
      try {
        const resp = await OfficeService.updateRequestRecipients(name, emailOrFax, request.requestId, request.officeId);
        setIsLoading(false);
        if (resp !== null) {
          onClickCancel(true);
          dispatch(setRequestData(resp));
          toast?.openToast("Updated successfully!", 2000, ToastVariants.Success);
        } else {
          toast?.openToast("Updation failed!");
        }
      } catch (_) {
        setIsLoading(false);
        toast?.openToast("Something went wrong please try again!", 2000, ToastVariants.Error);
      }
    }
  };

  return (
    <>
      {assignRequestError ? <p className="text-red-400 text-center mt-3">An error has occurred, please try again</p> : null}

      {/* Company Search */}
      <div className="px-8 py-2">
        <SelectWithBackendSearch
          label="Search Company"
          value={selectedContact?.company ? `${selectedContact?.company}` : ""}
          onSelect={(option: any) => handleContactSelect(option as Contact)}
          options={officeContacts || []}
          placeholder={"Select Company"}
          searchValue={searchValue}
          onSearchChange={(e, newValue) => setSearchValue(newValue)}
          loading={contactsLoading}
          renderOption={(option: Contact) => (
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0 mr-2">
                <span className="font-medium text-docsigna-blue-dark">{option?.company || "N/A"}</span>
              </div>
              <div className="flex-1 min-w-0 text-right">
                <span className="text-docsigna-blue-dark text-sm">{truncateText(formatAddress(option), 40)}</span>
              </div>
            </div>
          )}
          renderValue={option => {
            const renderVal = officeContacts?.find(o => `${o?.company}` === option);
            return renderVal ? `${renderVal?.company}` : "";
          }}
        />
      </div>

      {/* Contact Method Selection */}
      {selectedContact && (selectedContact.email || selectedContact.fax) && (
        <div className="px-8 py-2">
          <p className="text-sm font-medium text-docsigna-blue-dark mb-2">Select method for sending final document:</p>
          <div className="flex gap-4">
            {selectedContact.email && (
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={selectedContactMethod === ContactMethodType.Email}
                  value={ContactMethodType.Email}
                  onChange={e => handleContactMethod(e.currentTarget.value as ContactMethodType)}
                  className="general-radio bg-transparent"
                />
                <span className="text-base ml-2">Email</span>
              </label>
            )}
            {selectedContact.fax && (
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={selectedContactMethod === ContactMethodType.Fax}
                  value={ContactMethodType.Fax}
                  onChange={e => handleContactMethod(e.currentTarget.value as ContactMethodType)}
                  className="general-radio bg-transparent"
                />
                <span className="text-base ml-2">Fax</span>
              </label>
            )}
          </div>
        </div>
      )}

      {/* Manual Entry Fields */}
      <div className="px-8 py-2">
        <TextInput
          onChangeFunc={e => handleNameChange(e.currentTarget.value)}
          value={name}
          label={"Recipient Name"}
          placeholder="Enter name"
          errorMsg="Please provide recipient name"
          isError={errors.name && errorInitiated}
          onBlur={e => handleNameChange(e.currentTarget.value.trim())}
          isDarkBg={true}
        />
      </div>
      <div className="px-8 py-2">
        <TextInput
          onChangeFunc={e => handleEmailOrFaxInput(e, e.target.selectionEnd)}
          value={emailOrFax}
          label="Email or Fax Number"
          placeholder="Enter email address or fax number"
          errorMsg="Please provide valid email address or fax number with format 111-222-333"
          isError={errors.emailOrFax && errorInitiated}
          isDarkBg={true}
        />
      </div>

      <div className="fixed bottom-0 right-0 w-11/12 max-w-120 z-30 bg-docsigna-pink-light">
        <div className="px-8 py-5 flex justify-end">
          <Button onClick={() => onClickCancel()} className="pointer px-5 mr-3" text="Cancel" width="fit" variant="Secondary" disabled={isLoading} />
          <div>
            <Button
              onClick={() => {
                setErrorInitiated(true);
                if (validateForm()) {
                  saveRecipient();
                }
              }}
              variant={loading ? "loading" : "Primary"}
              disabled={(!validateForm() && errorInitiated) || isLoading}
              className="pointer px-5"
              text="Save"
              width="fit"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Recipients;
